/**
 * Display component for cascade data
 */

import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { DetentData } from '../types';
import { useLensProjection } from '../hooks/useLensProjection';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import styles from './CascadeStack.module.css';

const MIN_AUTOSCALE_FONT = 8;

interface CascadeStackProps {
  detent: DetentData;
  lensId: string;
  position: number;
  onPositionChange?: (position: number) => void;
  onDetentChange?: (detentId: string) => void;
}

export const CascadeStack: React.FC<CascadeStackProps> = ({
  detent,
  lensId,
  position,
  onPositionChange,
  onDetentChange,
}) => {
  const { isDarkMode } = useTheme();
  const { settings } = useSettings();
  const stackRef = useRef<HTMLDivElement>(null);
  const linesRootRef = useRef<HTMLDivElement>(null);
  const [autoFontSize, setAutoFontSize] = useState<number | null>(null);

  // Touch gesture tracking
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);

  // Detents list for navigation
  const [allDetents, setAllDetents] = useState<string[]>([]);

  // Fetch detents list on mount
  React.useEffect(() => {
    const fetchDetents = async () => {
      try {
        const response = await fetch('/data/detents.json');
        const data = await response.json();

        // Combine years and zany detents into single array
        const years = data.years?.map((y: number) => y.toString()) || [];
        const zany = data.zany?.map((z: { id: string }) => z.id) || [];
        setAllDetents([...years, ...zany]);
      } catch (error) {
        console.error('Failed to fetch detents:', error);
      }
    };

    fetchDetents();
  }, []);

  // Handle arrow key navigation for slider control
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onPositionChange) return;

    const step = 0.01; // 1% steps
    let newPosition = position;

    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        e.preventDefault();
        newPosition = Math.min(1, position + step);
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        e.preventDefault();
        newPosition = Math.max(0, position - step);
        break;
      default:
        return;
    }

    if (newPosition !== position) {
      onPositionChange(newPosition);
    }
  };

  // Handle touch start for swipe gestures
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    setIsSwiping(false);
  };

  // Handle touch move to detect swipe direction
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!touchStartRef.current) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

    // Horizontal swipe detection (works naturally - portrait mode rotates the whole UI)
    if (deltaX > deltaY && deltaX > 10) {
      setIsSwiping(true);
      // Prevent scrolling while swiping to navigate
      e.preventDefault();
    }
  };

  // Handle touch end to navigate between detents
  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!touchStartRef.current || !isSwiping) {
      touchStartRef.current = null;
      setIsSwiping(false);
      return;
    }

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

    // Horizontal swipe to change detents (works naturally - portrait mode rotates the whole UI)
    if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 50) {
      if (onDetentChange && allDetents.length > 0) {
        const currentIndex = allDetents.indexOf(detent.id);
        if (currentIndex !== -1) {
          let newIndex;

          if (deltaX > 0) {
            // Swipe right - go to previous detent
            newIndex = currentIndex > 0 ? currentIndex - 1 : allDetents.length - 1;
          } else {
            // Swipe left - go to next detent
            newIndex = currentIndex < allDetents.length - 1 ? currentIndex + 1 : 0;
          }

          onDetentChange(allDetents[newIndex]);
        }
      }
    }

    touchStartRef.current = null;
    setIsSwiping(false);
  };

  // Get all blocks from the detent
  const blocks = detent.blocks || [];

  // Find the active block (for now, just use the first block)
  // TODO: Implement logic to select the appropriate block based on UI state
  const activeBlock = blocks[0] || null;

  // Apply lens projection to the active block
  const { projectedLines, error } = useLensProjection(
    lensId,
    position,
    activeBlock
  );

  useLayoutEffect(() => {
    if (!settings.autoscaleLines) {
      setAutoFontSize(null);
      return;
    }

    const container = stackRef.current;
    const linesRoot = linesRootRef.current;
    if (!container || !linesRoot) {
      return;
    }

    const textNodes = Array.from(linesRoot.querySelectorAll(`.${styles.lineText}`));
    if (textNodes.length === 0) {
      setAutoFontSize(settings.fontSize);
      return;
    }

    const hasWrap = () =>
      textNodes.some(node => node.scrollHeight > node.clientHeight + 1);

    const originalFont = container.style.fontSize;
    let testSize = settings.fontSize;
    let resolvedSize = settings.fontSize;

    while (testSize >= MIN_AUTOSCALE_FONT) {
      container.style.fontSize = `${testSize}px`;
      if (!hasWrap()) {
        resolvedSize = testSize;
        break;
      }
      resolvedSize = Math.max(MIN_AUTOSCALE_FONT, testSize - 1);
      testSize -= 1;
    }

    container.style.fontSize = originalFont;
    setAutoFontSize(prev => (prev === resolvedSize ? prev : resolvedSize));
  }, [settings.autoscaleLines, settings.fontSize, projectedLines, activeBlock?.id, position]);


  // Memoize the rendered lines to avoid unnecessary re-renders
  const visibleLines = settings.maxLines > 0
    ? projectedLines.slice(0, settings.maxLines)
    : projectedLines;

  const renderedLines = useMemo(() => {
    return visibleLines.map((line, index) => {
      const isEven = index % 2 === 0;
      const lineClasses = `${styles.lineRow} ${
        settings.alternatingLines
          ? isEven
            ? styles.lineEven
            : styles.lineOdd
          : ''
      } ${settings.animationEnabled ? styles.animated : ''}`;

      return (
        <div
          key={`${activeBlock?.id}-${index}`}
          className={lineClasses}
          style={{
            animationDelay: settings.animationEnabled ? `${index * 0.05}s` : '0s',
          }}
        >
          <div className={styles.lineNumber}>{index + 1}</div>
          <div className={styles.lineText}>{line}</div>
        </div>
      );
    });
  }, [visibleLines, activeBlock?.id, settings.alternatingLines, settings.animationEnabled]);

  if (error) {
    return (
      <div className={`${styles.container} ${isDarkMode ? styles.dark : styles.light}`}>
        <div className={styles.error}>
          Projection error: {error}
        </div>
      </div>
    );
  }

  if (!activeBlock) {
    return (
      <div className={`${styles.container} ${isDarkMode ? styles.dark : styles.light}`}>
        <div className={styles.empty}>
          No cascade data available
        </div>
      </div>
    );
  }

  // Apply custom font and size styles
  const effectiveFontSize = settings.autoscaleLines && autoFontSize
    ? autoFontSize
    : settings.fontSize;

  const containerStyle = {
    fontFamily: settings.fontFamily,
    fontSize: `${effectiveFontSize}px`,
  };

  const linesContainerStyle = {
    overflowX: settings.horizontalScroll ? 'auto' as const : 'hidden' as const,
    overflowY: 'auto' as const,
  };

  return (
    <div
      className={`${styles.container} ${isDarkMode ? styles.dark : styles.light}`}
      ref={stackRef}
      style={containerStyle}
    >
      <div
        className={styles.linesContainer}
        style={linesContainerStyle}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="region"
        aria-label="Cascade text display"
      >
        {renderedLines.length > 0 ? (
          <div
            className={`${styles.lines} ${settings.horizontalScroll ? styles.noWrap : ''}`}
            ref={linesRootRef}
          >
            {renderedLines}
          </div>
        ) : (
          <div className={styles.empty}>
            No lines to display at this position
          </div>
        )}
      </div>
      {activeBlock?.meta?.model ? (
        <div className={styles.modelInfo}>
          generated by {String(activeBlock.meta.model)}
        </div>
      ) : null}
    </div>
  );
};
